/**
 * InterviewChecklist.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Checklist dinámico de entrevista para A La Vuelta 360°
 * Template seleccionado automáticamente según el puesto del candidato.
 * Guardado persistente en DB (campo checklist_data en postulaciones).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Typography, Checkbox, FormControlLabel, TextField, Chip, Button,
  CircularProgress, Divider, Collapse, Tooltip,
} from "@mui/material";
import {
  ExpandMore, Save, CloudDone, PictureAsPdf,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const NIVELES_EVALUACION = [
  { id: "excelente",      label: "Excelente candidato",   emoji: "🌟", color: "#22c55e", desc: "Perfil ideal — contratar" },
  { id: "viable",         label: "Candidato viable",      emoji: "👍", color: "#3b82f6", desc: "Cumple requisitos" },
  { id: "seguimiento",    label: "Requiere seguimiento",  emoji: "🔄", color: "#f59e0b", desc: "Potencial a valorar" },
  { id: "no_recomendado", label: "No recomendado",        emoji: "⛔", color: "#ef4444", desc: "No cumple el perfil" },
];

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATES DE CHECKLIST POR PUESTO
// ─────────────────────────────────────────────────────────────────────────────
const TEMPLATES = {

  // ── PROMOTOR ───────────────────────────────────────────────────────────────
  promotor: {
    icon: "🎤",
    titulo: "Promotor(a) de Eventos",
    color: "#f97316",
    secciones: [
      {
        id: "datos_generales",
        titulo: "Datos Generales del Candidato",
        emoji: "📋",
        items: [
          { id: "nombre",             label: "Nombre completo verificado" },
          { id: "edad",               label: "Edad" },
          { id: "telefono",           label: "Teléfono de contacto" },
          { id: "email",              label: "Correo electrónico" },
          { id: "zona",               label: "Zona donde vive" },
          { id: "disponibilidad",     label: "Disponibilidad de horario" },
          { id: "transporte",         label: "Cuenta con transporte propio" },
          { id: "redes_sociales",     label: "Manejo de redes sociales" },
          { id: "exp_ventas",         label: "Experiencia en ventas o eventos" },
          { id: "presentacion",       label: "Presentación personal adecuada" },
        ],
      },
      {
        id: "actitud_personalidad",
        titulo: "Actitud y Personalidad",
        emoji: "✨",
        items: [
          { id: "facilidad_palabra",  label: "Facilidad de palabra" },
          { id: "actitud_positiva",   label: "Actitud positiva" },
          { id: "seguridad",          label: "Seguridad al comunicarse" },
          { id: "responsable",        label: "Responsable y puntual" },
          { id: "proactivo",          label: "Proactivo(a)" },
          { id: "atencion_cliente",   label: "Buena atención al cliente" },
          { id: "trabajo_equipo",     label: "Trabajo en equipo" },
          { id: "tolerancia_presion", label: "Tolerancia a trabajar bajo presión" },
        ],
      },
      {
        id: "habilidades_comerciales",
        titulo: "Habilidades Comerciales",
        emoji: "💼",
        items: [
          { id: "gusto_ventas",       label: "Gusto por las ventas" },
          { id: "negociacion",        label: "Capacidad de negociación" },
          { id: "generar_confianza",  label: "Facilidad para generar confianza" },
          { id: "cerrar_ventas",      label: "Capacidad para cerrar ventas" },
          { id: "seguimiento",        label: "Seguimiento a clientes" },
        ],
      },
      {
        id: "imagen_presencia",
        titulo: "Imagen y Presencia",
        emoji: "👔",
        items: [
          { id: "imagen_profesional", label: "Imagen profesional adecuada" },
          { id: "lenguaje",           label: "Lenguaje adecuado y profesional" },
          { id: "energia",            label: "Buena energía y entusiasmo" },
        ],
      },
      {
        id: "disponibilidad_operativa",
        titulo: "Disponibilidad Operativa",
        emoji: "📅",
        items: [
          { id: "fines_semana",       label: "Puede trabajar fines de semana" },
          { id: "nocturnos",          label: "Disponible para eventos nocturnos" },
          { id: "traslados",          label: "Disponible para traslados en CDMX y Estado de México" },
          { id: "inmediata",          label: "Disponibilidad inmediata" },
          { id: "por_evento",         label: "Puede trabajar por evento / comisión" },
        ],
      },
      {
        id: "conocimiento_digital",
        titulo: "Conocimiento Digital",
        emoji: "📱",
        items: [
          { id: "whatsapp_business",  label: "Uso de WhatsApp Business" },
          { id: "redes",              label: "Instagram / Facebook / TikTok básico" },
          { id: "celular_compu",      label: "Manejo básico de celular / computadora" },
          { id: "cotizaciones",       label: "Facilidad para enviar cotizaciones o seguimiento" },
        ],
      },
    ],
    preguntas: [
      {
        categoria: "Sobre su personalidad",
        emoji: "😊",
        items: [
          "Cuéntame un poco sobre ti.",
          "¿Por qué te interesa trabajar en A La Vuelta 360°?",
          "¿Cómo te describirían las personas que trabajan contigo?",
          "¿Qué es lo que más disfrutas al tratar con clientes?",
        ],
      },
      {
        categoria: "Sobre ventas y atención al cliente",
        emoji: "💬",
        items: [
          "¿Has trabajado anteriormente en ventas o atención al cliente?",
          "¿Cómo reaccionas cuando un cliente no está interesado?",
          "¿Cómo generarías confianza con un cliente nuevo?",
          "¿Qué harías para conseguir más clientes o eventos?",
        ],
      },
      {
        categoria: "Sobre eventos",
        emoji: "🎪",
        items: [
          "¿Has trabajado en eventos anteriormente?",
          "¿Cómo manejarías una situación complicada durante un evento?",
          "¿Te sientes cómodo(a) hablando con muchas personas?",
        ],
      },
      {
        categoria: "Sobre compromiso y responsabilidad",
        emoji: "🎯",
        items: [
          "¿Cómo organizas tus tiempos y actividades?",
          "¿Qué harías si surge un problema minutos antes de un evento?",
          "¿Qué significa para ti trabajar en equipo?",
        ],
      },
      {
        categoria: "Sobre metas y motivación",
        emoji: "🌟",
        items: [
          "¿Qué te motiva a trabajar?",
          "¿Qué metas personales o profesionales tienes?",
          "¿Qué esperas aprender o lograr en este puesto?",
        ],
      },
    ],
  },

  // ── COORDINADOR ────────────────────────────────────────────────────────────
  coordinador: {
    icon: "📊",
    titulo: "Coordinador(a) de Eventos",
    color: "#6366f1",
    secciones: [
      {
        id: "datos_generales",
        titulo: "Datos Generales",
        emoji: "📋",
        items: [
          { id: "nombre",         label: "Nombre completo verificado" },
          { id: "contacto",       label: "Datos de contacto verificados" },
          { id: "zona",           label: "Zona de residencia" },
          { id: "transporte",     label: "Cuenta con transporte propio" },
          { id: "disponibilidad", label: "Disponibilidad horaria clara" },
          { id: "presentacion",   label: "Presentación profesional adecuada" },
        ],
      },
      {
        id: "competencias_gestion",
        titulo: "Competencias de Gestión",
        emoji: "🗂️",
        items: [
          { id: "organizacion",     label: "Habilidades de organización" },
          { id: "planificacion",    label: "Capacidad de planificación y anticipación" },
          { id: "multitarea",       label: "Manejo de múltiples tareas simultáneas" },
          { id: "toma_decisiones",  label: "Toma de decisiones bajo presión" },
          { id: "proveedores",      label: "Experiencia con proveedores" },
          { id: "presupuestos",     label: "Manejo básico de presupuestos" },
        ],
      },
      {
        id: "liderazgo_comunicacion",
        titulo: "Liderazgo y Comunicación",
        emoji: "🧩",
        items: [
          { id: "liderazgo",           label: "Capacidad de liderazgo" },
          { id: "comunicacion",        label: "Comunicación clara y efectiva" },
          { id: "trabajo_equipo",      label: "Trabajo en equipo" },
          { id: "resolucion",          label: "Resolución de conflictos" },
          { id: "orientacion_servicio",label: "Orientación al servicio al cliente" },
        ],
      },
      {
        id: "logistica_operacion",
        titulo: "Logística y Operación",
        emoji: "🔧",
        items: [
          { id: "montaje",         label: "Experiencia en montaje / desmontaje de eventos" },
          { id: "protocolos",      label: "Conocimiento de protocolos de eventos" },
          { id: "contingencias",   label: "Manejo de contingencias en campo" },
          { id: "coord_staff",     label: "Coordinación de equipos en campo" },
        ],
      },
      {
        id: "disponibilidad_operativa",
        titulo: "Disponibilidad Operativa",
        emoji: "📅",
        items: [
          { id: "fines_semana",   label: "Disponible fines de semana y días festivos" },
          { id: "nocturnos",      label: "Disponible para eventos nocturnos" },
          { id: "traslados",      label: "Disponible para traslados en CDMX y Estado de México" },
          { id: "inmediata",      label: "Disponibilidad inmediata" },
        ],
      },
      {
        id: "herramientas_digitales",
        titulo: "Herramientas Digitales",
        emoji: "💻",
        items: [
          { id: "excel",      label: "Excel / Google Sheets" },
          { id: "whatsapp",   label: "Gestión de grupos y comunicación por WhatsApp" },
          { id: "drive",      label: "Google Drive / herramientas colaborativas" },
          { id: "redes",      label: "Manejo básico de redes sociales" },
        ],
      },
    ],
    preguntas: [
      {
        categoria: "Experiencia y gestión",
        emoji: "📋",
        items: [
          "¿Cuál ha sido el evento más grande que has coordinado?",
          "¿Cómo manejas los imprevistos durante un evento?",
          "¿Cómo organizas tu día cuando tienes múltiples tareas simultáneas?",
          "Cuéntame de una situación difícil y cómo la resolviste.",
        ],
      },
      {
        categoria: "Liderazgo",
        emoji: "🧩",
        items: [
          "¿Cómo motivas a tu equipo cuando hay mucha presión?",
          "¿Cómo manejas conflictos entre miembros del equipo?",
          "¿Qué haces cuando un proveedor no llega a tiempo?",
        ],
      },
      {
        categoria: "Servicio al cliente",
        emoji: "⭐",
        items: [
          "¿Cómo aseguras que el cliente quede satisfecho con el evento?",
          "¿Cómo manejas a un cliente difícil o muy exigente?",
          "¿Qué harías si el cliente cambia los requerimientos a último momento?",
        ],
      },
    ],
  },

  // ── FOTÓGRAFO / CAMARÓGRAFO ────────────────────────────────────────────────
  fotografo: {
    icon: "📷",
    titulo: "Fotógrafo / Camarógrafo",
    color: "#ec4899",
    secciones: [
      {
        id: "datos_generales",
        titulo: "Datos Generales",
        emoji: "📋",
        items: [
          { id: "nombre",      label: "Nombre completo verificado" },
          { id: "contacto",    label: "Datos de contacto verificados" },
          { id: "zona",        label: "Zona de residencia" },
          { id: "transporte",  label: "Cuenta con transporte propio" },
          { id: "portfolio",   label: "Tiene portafolio / muestras de trabajo disponibles" },
        ],
      },
      {
        id: "habilidades_tecnicas",
        titulo: "Habilidades Técnicas",
        emoji: "🎞️",
        items: [
          { id: "fotografia",     label: "Fotografía profesional" },
          { id: "video",          label: "Videografía / grabación de video" },
          { id: "edicion_foto",   label: "Edición de fotografía (Lightroom / Photoshop)" },
          { id: "edicion_video",  label: "Edición de video (Premiere / Final Cut / CapCut)" },
          { id: "equipo_propio",  label: "Cuenta con equipo propio (cámara, lentes, etc.)" },
          { id: "iluminacion",    label: "Manejo de iluminación en eventos" },
          { id: "audio_video",    label: "Conocimiento básico de audio en video" },
          { id: "drone",          label: "Manejo de drone (si aplica)" },
        ],
      },
      {
        id: "experiencia_eventos",
        titulo: "Experiencia en Eventos",
        emoji: "🎪",
        items: [
          { id: "sociales",       label: "Eventos sociales (bodas, XV, graduaciones)" },
          { id: "corporativos",   label: "Eventos corporativos" },
          { id: "infantiles",     label: "Fiestas infantiles" },
          { id: "vivo",           label: "Trabaja bien bajo presión en eventos en vivo" },
          { id: "espontaneo",     label: "Habilidad para capturar momentos espontáneos" },
        ],
      },
      {
        id: "entrega_calidad",
        titulo: "Tiempos de Entrega y Calidad",
        emoji: "⏱️",
        items: [
          { id: "tiempos",     label: "Tiempo de entrega razonable (48–72 hrs fotos, 1 sem video)" },
          { id: "formatos",    label: "Entrega en formatos requeridos (JPG alta res., MP4)" },
          { id: "sin_marcas",  label: "Entrega sin marcas de agua" },
          { id: "revisiones",  label: "Disponible para revisiones y ajustes" },
        ],
      },
      {
        id: "disponibilidad_operativa",
        titulo: "Disponibilidad Operativa",
        emoji: "📅",
        items: [
          { id: "fines_semana", label: "Disponible fines de semana" },
          { id: "nocturnos",    label: "Disponible para eventos nocturnos" },
          { id: "traslados",    label: "Disponible para traslados en CDMX y Estado de México" },
          { id: "inmediata",    label: "Disponibilidad inmediata / por proyecto" },
        ],
      },
    ],
    preguntas: [
      {
        categoria: "Trabajo y portafolio",
        emoji: "📸",
        items: [
          "¿Puedes mostrarnos tu portafolio o trabajo reciente?",
          "¿Qué tipo de eventos has fotografiado / filmado más?",
          "¿Cómo describirías tu estilo fotográfico?",
          "¿Con qué equipo trabajas actualmente?",
        ],
      },
      {
        categoria: "Situaciones en eventos",
        emoji: "🎬",
        items: [
          "¿Qué haces cuando la iluminación no es favorable?",
          "¿Cómo manejas cuando el cliente no queda satisfecho con las fotos?",
          "¿Cómo te adaptas cuando el cronograma del evento cambia?",
        ],
      },
      {
        categoria: "Compromisos y disponibilidad",
        emoji: "✅",
        items: [
          "¿Cuáles son tus tiempos habituales de entrega?",
          "¿Tienes contratos de exclusividad con otras agencias?",
          "¿Estás disponible para trabajar por proyecto con nosotros?",
        ],
      },
    ],
  },

  // ── DJ / SONIDISTA ─────────────────────────────────────────────────────────
  dj: {
    icon: "🎧",
    titulo: "DJ / Sonidista",
    color: "#a855f7",
    secciones: [
      {
        id: "datos_generales",
        titulo: "Datos Generales",
        emoji: "📋",
        items: [
          { id: "nombre",     label: "Nombre completo verificado" },
          { id: "contacto",   label: "Datos de contacto verificados" },
          { id: "zona",       label: "Zona de residencia" },
          { id: "transporte", label: "Cuenta con transporte (considera traslado de equipo)" },
          { id: "demo",       label: "Tiene demo / mix disponible para escuchar" },
        ],
      },
      {
        id: "habilidades_tecnicas",
        titulo: "Habilidades Técnicas",
        emoji: "🔊",
        items: [
          { id: "mezcla",          label: "Habilidad de mezcla y transición" },
          { id: "lectura_pista",   label: "Lectura de pista / público" },
          { id: "generos",         label: "Versatilidad en géneros musicales" },
          { id: "controladora",    label: "Manejo de controladora / CDJ" },
          { id: "software",        label: "Software DJ (Serato / Traktor / Rekordbox)" },
          { id: "pa",              label: "Conocimiento básico de sistema de sonido (PA)" },
          { id: "iluminacion_dj",  label: "Manejo básico de iluminación DJ (si aplica)" },
        ],
      },
      {
        id: "equipo_materiales",
        titulo: "Equipo y Materiales",
        emoji: "🎛️",
        items: [
          { id: "equipo_propio", label: "Equipo propio (controladora, laptop, cables)" },
          { id: "bafles",        label: "Bafles / amplificadores (si aplica)" },
          { id: "backup",        label: "Equipo de respaldo ante fallas" },
          { id: "libreria",      label: "Biblioteca musical actualizada" },
        ],
      },
      {
        id: "experiencia_eventos",
        titulo: "Experiencia en Eventos",
        emoji: "🎪",
        items: [
          { id: "sociales",      label: "Eventos sociales (bodas, XV, graduaciones)" },
          { id: "corporativos",  label: "Eventos corporativos" },
          { id: "bares",         label: "Bares / antros / terrazas" },
          { id: "con_mc",        label: "Trabajo coordinado con MC / animador" },
        ],
      },
      {
        id: "disponibilidad_operativa",
        titulo: "Disponibilidad Operativa",
        emoji: "📅",
        items: [
          { id: "fines_semana", label: "Disponible fines de semana" },
          { id: "nocturnos",    label: "Disponible para eventos nocturnos" },
          { id: "traslados",    label: "Disponible para traslados en CDMX y Estado de México" },
          { id: "freelance",    label: "Disponible para trabajar como freelance / por evento" },
        ],
      },
    ],
    preguntas: [
      {
        categoria: "Música y experiencia",
        emoji: "🎵",
        items: [
          "¿Qué tipo de eventos has atendido más?",
          "¿Cómo describes tu estilo musical?",
          "¿Tienes algún demo o mix reciente que podamos escuchar?",
          "¿Qué géneros dominas mejor?",
        ],
      },
      {
        categoria: "Situaciones en eventos",
        emoji: "⚡",
        items: [
          "¿Qué haces cuando el público no responde a la música?",
          "¿Cómo manejas una falla técnica durante el evento?",
          "¿Cómo coordinas con el maestro de ceremonias?",
        ],
      },
      {
        categoria: "Logística y compromisos",
        emoji: "🔧",
        items: [
          "¿Cuánto tiempo necesitas para instalar y hacer pruebas de sonido?",
          "¿Tienes equipo de respaldo en caso de falla?",
          "¿Tienes exclusividad con alguna otra agencia?",
        ],
      },
    ],
  },

  // ── STAFF / LOGÍSTICA ──────────────────────────────────────────────────────
  staff: {
    icon: "👥",
    titulo: "Staff / Logística",
    color: "#14b8a6",
    secciones: [
      {
        id: "datos_generales",
        titulo: "Datos Generales",
        emoji: "📋",
        items: [
          { id: "nombre",       label: "Nombre completo verificado" },
          { id: "edad",         label: "Edad" },
          { id: "contacto",     label: "Datos de contacto verificados" },
          { id: "zona",         label: "Zona de residencia" },
          { id: "transporte",   label: "Cuenta con transporte propio" },
          { id: "presentacion", label: "Presentación personal adecuada" },
        ],
      },
      {
        id: "condicion_actitud",
        titulo: "Condición y Actitud",
        emoji: "💪",
        items: [
          { id: "condicion_fisica", label: "Buena condición física para trabajo en campo" },
          { id: "trabajo_pie",      label: "Puede trabajar largas jornadas de pie" },
          { id: "carga",            label: "Disponible para carga y descarga de materiales" },
          { id: "actitud_positiva", label: "Actitud positiva y servicial" },
          { id: "trabajo_equipo",   label: "Trabajo en equipo" },
          { id: "presion",          label: "Tolerancia a trabajar bajo presión" },
          { id: "puntualidad",      label: "Puntualidad y responsabilidad" },
        ],
      },
      {
        id: "experiencia",
        titulo: "Experiencia",
        emoji: "🎪",
        items: [
          { id: "eventos",    label: "Experiencia previa en eventos (deseable)" },
          { id: "montaje",    label: "Experiencia en montaje / desmontaje" },
          { id: "recepcion",  label: "Experiencia en recepción o atención a invitados" },
          { id: "materiales", label: "Manejo y cuidado de materiales y equipo" },
        ],
      },
      {
        id: "disponibilidad_operativa",
        titulo: "Disponibilidad Operativa",
        emoji: "📅",
        items: [
          { id: "fines_semana", label: "Disponible fines de semana" },
          { id: "nocturnos",    label: "Disponible para eventos nocturnos" },
          { id: "traslados",    label: "Disponible para traslados en CDMX y Estado de México" },
          { id: "inmediata",    label: "Disponibilidad inmediata" },
          { id: "por_evento",   label: "Acepta trabajar por evento / día" },
        ],
      },
    ],
    preguntas: [
      {
        categoria: "Experiencia",
        emoji: "🛠️",
        items: [
          "¿Has trabajado en eventos anteriormente? ¿En qué puesto?",
          "¿Estás familiarizado con montaje de carpas, mesas, sillas?",
          "¿Cómo te describes como trabajador(a)?",
        ],
      },
      {
        categoria: "Disposición",
        emoji: "💼",
        items: [
          "¿Estás dispuesto(a) a trabajar en horarios nocturnos?",
          "¿Cómo reaccionas cuando hay cambios de último momento?",
          "¿Tienes alguna limitación física que debamos considerar?",
        ],
      },
    ],
  },
};

// Template por defecto (puesto no identificado)
const TEMPLATE_DEFAULT = {
  icon: "📋",
  titulo: "Entrevista General",
  color: "#6366f1",
  secciones: [
    {
      id: "datos_generales",
      titulo: "Datos Generales",
      emoji: "📋",
      items: [
        { id: "nombre",       label: "Nombre completo verificado" },
        { id: "edad",         label: "Edad" },
        { id: "contacto",     label: "Datos de contacto verificados" },
        { id: "zona",         label: "Zona de residencia" },
        { id: "transporte",   label: "Cuenta con transporte propio" },
        { id: "disponibilidad", label: "Disponibilidad horaria" },
        { id: "presentacion", label: "Presentación personal adecuada" },
      ],
    },
    {
      id: "perfil_profesional",
      titulo: "Perfil Profesional",
      emoji: "💼",
      items: [
        { id: "experiencia",  label: "Experiencia relevante para el puesto" },
        { id: "habilidades",  label: "Habilidades técnicas aplicables" },
        { id: "logros",       label: "Logros o proyectos anteriores destacados" },
        { id: "presion",      label: "Trabajo bajo presión" },
        { id: "aprendizaje",  label: "Disposición para aprender" },
      ],
    },
    {
      id: "actitud_valores",
      titulo: "Actitud y Valores",
      emoji: "⭐",
      items: [
        { id: "puntualidad",    label: "Puntualidad y responsabilidad" },
        { id: "actitud",        label: "Actitud positiva y proactiva" },
        { id: "comunicacion",   label: "Comunicación efectiva" },
        { id: "trabajo_equipo", label: "Trabajo en equipo" },
        { id: "compromiso",     label: "Compromiso con el trabajo" },
      ],
    },
    {
      id: "disponibilidad_operativa",
      titulo: "Disponibilidad Operativa",
      emoji: "📅",
      items: [
        { id: "horario",      label: "Horario de disponibilidad claro" },
        { id: "fines_semana", label: "Disponible fines de semana (si aplica)" },
        { id: "inmediata",    label: "Disponibilidad inmediata" },
        { id: "traslados",    label: "Disponible para traslados (si aplica)" },
      ],
    },
  ],
  preguntas: [
    {
      categoria: "Presentación personal",
      emoji: "😊",
      items: [
        "Cuéntame un poco sobre ti.",
        "¿Por qué te interesa trabajar en A La Vuelta 360°?",
        "¿Qué es lo que más disfrutas de tu trabajo?",
      ],
    },
    {
      categoria: "Experiencia y habilidades",
      emoji: "💼",
      items: [
        "¿Cuál ha sido tu mayor logro en un trabajo anterior?",
        "¿Cómo manejas situaciones de estrés o presión?",
        "¿Qué te diferencia de otros candidatos?",
      ],
    },
    {
      categoria: "Motivación y metas",
      emoji: "🎯",
      items: [
        "¿Qué te motiva a trabajar?",
        "¿Dónde te ves en 2 años?",
        "¿Qué esperas aprender en este puesto?",
      ],
    },
  ],
};

// ── Selección automática de template ──────────────────────────────────────────
export function getTemplate(puesto = "") {
  const p = puesto.toLowerCase();
  if (/(promot|vendedor.*event|impulsor)/.test(p))     return TEMPLATES.promotor;
  if (/(coordin)/.test(p))                             return TEMPLATES.coordinador;
  if (/(foto|cámara|camara|video|cinemat|audiovis)/.test(p)) return TEMPLATES.fotografo;
  if (/(dj|sonid|audio|músic|music|iluminac)/.test(p)) return TEMPLATES.dj;
  if (/(staff|logis|asistent|ayudan|auxiliar|operador)/.test(p)) return TEMPLATES.staff;
  return TEMPLATE_DEFAULT;
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTES
// ─────────────────────────────────────────────────────────────────────────────

// Sección con acordeón
const SeccionChecklist = ({ seccion, checks, onToggle, notes, onNoteChange, templateColor }) => {
  const [expanded, setExpanded] = useState(true);

  const checked  = seccion.items.filter(i => checks[`${seccion.id}.${i.id}`]).length;
  const total    = seccion.items.length;
  const complete = checked === total;

  return (
    <Box sx={{
      border: `1px solid ${complete ? "rgba(34,197,94,0.28)" : "rgba(255,255,255,0.08)"}`,
      borderRadius: "14px",
      background: complete ? "rgba(34,197,94,0.03)" : "rgba(255,255,255,0.02)",
      transition: "border-color 0.3s, background 0.3s",
      overflow: "hidden",
    }}>
      {/* Header */}
      <Box
        onClick={() => setExpanded(p => !p)}
        sx={{
          px: 2, py: 1.4, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
          "&:hover": { background: "rgba(255,255,255,0.03)" }, transition: "background 0.15s",
        }}
      >
        <Typography sx={{ fontSize: "1.05rem", lineHeight: 1, flexShrink: 0 }}>{seccion.emoji}</Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.84rem", lineHeight: 1.2 }}>
            {seccion.titulo}
          </Typography>
          <Typography variant="caption" sx={{ color: complete ? "#22c55e" : "text.disabled", fontSize: "0.71rem" }}>
            {checked}/{total} {complete ? "✓ Completada" : "ítems evaluados"}
          </Typography>
        </Box>
        {/* Mini barra de progreso */}
        <Box sx={{ width: 44, height: 3.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.07)", flexShrink: 0 }}>
          <Box sx={{
            height: "100%", borderRadius: 2, transition: "width 0.3s",
            width: `${(checked / total) * 100}%`,
            background: complete ? "#22c55e" : templateColor,
          }} />
        </Box>
        <Box sx={{
          color: "text.disabled", display: "flex", alignItems: "center",
          transition: "transform 0.25s", transform: expanded ? "rotate(180deg)" : "none",
        }}>
          <ExpandMore sx={{ fontSize: 17 }} />
        </Box>
      </Box>

      {/* Items */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          {seccion.items.map((item) => {
            const key     = `${seccion.id}.${item.id}`;
            const checked = !!checks[key];
            return (
              <FormControlLabel
                key={item.id}
                onClick={() => onToggle(seccion.id, item.id)}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => {}}
                    size="small"
                    sx={{
                      color: "rgba(255,255,255,0.18)",
                      "&.Mui-checked": { color: templateColor },
                      p: "4px 8px",
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{
                    fontSize: "0.82rem", lineHeight: 1.4,
                    color: checked ? "text.primary" : "rgba(255,255,255,0.55)",
                    transition: "color 0.2s",
                  }}>
                    {item.label}
                  </Typography>
                }
                sx={{
                  m: 0, py: 0.25, px: 0.5, width: "100%", borderRadius: "8px",
                  "&:hover": { background: "rgba(255,255,255,0.03)" }, transition: "background 0.15s",
                }}
              />
            );
          })}
          <TextField
            size="small" multiline rows={2} fullWidth
            placeholder={`Observaciones — ${seccion.titulo.toLowerCase()}`}
            value={notes || ""}
            onChange={e => onNoteChange(seccion.id, e.target.value)}
            sx={{
              mt: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", fontSize: "0.78rem", bgcolor: "rgba(255,255,255,0.02)",
                "& fieldset": { borderColor: "rgba(255,255,255,0.07)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.14)" },
                "&.Mui-focused fieldset": { borderColor: `${templateColor}70` },
              },
              "& textarea::placeholder": { color: "rgba(255,255,255,0.18)", fontSize: "0.75rem" },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
const InterviewChecklist = ({ candidato, initialData, onSave, saving }) => {
  const { user }  = useAuth();
  const entrevistador = user?.colaborador?.nombre_completo || user?.nombre_completo || user?.username || 'Recursos Humanos';

  const template = useMemo(
    () => getTemplate(candidato?.puesto_interes || ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidato?.id]
  );

  // ── Estado ──────────────────────────────────────────────────────────────────
  const [checks, setChecks]       = useState({});
  const [notes, setNotes]         = useState({});
  const [nivel, setNivel]         = useState(null);
  const [obsGen, setObsGen]       = useState("");
  const [dirty, setDirty]         = useState(false);

  // Cargar datos iniciales cada vez que cambia el candidato
  useEffect(() => {
    const d = initialData || {};
    setChecks(d.checks || {});
    setNotes(d.notes  || {});
    setNivel(d.nivel  || null);
    setObsGen(d.observacionesGenerales || "");
    setDirty(false);
  }, [candidato?.id, initialData]);

  // ── Progreso ────────────────────────────────────────────────────────────────
  const totalItems = useMemo(
    () => template.secciones.reduce((acc, s) => acc + s.items.length, 0),
    [template]
  );
  const checkedCount = useMemo(
    () => Object.values(checks).filter(Boolean).length,
    [checks]
  );
  const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
  const progressColor =
    progress === 100 ? "#22c55e" :
    progress >= 70   ? "#06b6d4" :
    progress >= 40   ? "#f59e0b" : template.color;

  // ── Validaciones para habilitar guardar ──────────────────────────────────────
  const sectionsWithoutCheck = useMemo(
    () => template.secciones.filter(sec =>
      !sec.items.some(item => !!checks[`${sec.id}.${item.id}`])
    ),
    [template.secciones, checks]
  );
  const allSectionsHaveCheck = sectionsWithoutCheck.length === 0;
  const hasObservaciones     = obsGen.trim().length > 0;
  const canSave              = dirty && allSectionsHaveCheck && hasObservaciones;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const toggleCheck = (sectionId, itemId) => {
    const key = `${sectionId}.${itemId}`;
    setChecks(prev => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  const handleNoteChange = (sectionId, value) => {
    setNotes(prev => ({ ...prev, [sectionId]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave({ checks, notes, nivel, observacionesGenerales: obsGen });
    setDirty(false);
  };

  // ── Generar PDF / Imprimir ficha ─────────────────────────────────────────────
  const handleGeneratePDF = () => {
    const win = window.open('', '_blank', 'width=1000,height=820');
    if (!win) { alert('Permite ventanas emergentes para generar el PDF.'); return; }

    const nivelData  = NIVELES_EVALUACION.find(n => n.id === nivel);
    const ahora      = new Date();
    const fechaGen   = ahora.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    const horaGen    = ahora.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const fechaPost  = candidato?.fecha_postulacion
      ? new Date(candidato.fecha_postulacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';
    const folio      = `RSE300${String(candidato?.id || 0).padStart(3, '0')}`;
    const iniciales  = (candidato?.nombre_completo || 'NN')
      .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    const accentColor = template.color;

    // ── Secciones del checklist ────────────────────────────────────────────
    const totalSecciones = template.secciones.length;
    let seccionesHtml = '';
    template.secciones.forEach((sec, si) => {
      const sectionNote  = notes[sec.id];
      const checkedLocal = sec.items.filter(it => !!checks[`${sec.id}.${it.id}`]).length;
      const pctSec       = Math.round((checkedLocal / sec.items.length) * 100);
      const isLast       = si === totalSecciones - 1;

      const cols = sec.items.length >= 8
        ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:0;">
            ${sec.items.map(item => {
              const checked = !!checks[`${sec.id}.${item.id}`];
              return `<div style="display:flex;align-items:center;padding:5px 8px;border-bottom:1px solid #f1f5f9;gap:7px;">
                <div style="flex-shrink:0;width:16px;height:16px;border-radius:4px;
                  background:${checked ? accentColor : '#e2e8f0'};
                  border:1.5px solid ${checked ? accentColor : '#cbd5e1'};
                  display:flex;align-items:center;justify-content:center;">
                  ${checked ? `<svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>` : ''}
                </div>
                <span style="font-size:11px;color:${checked ? '#0f172a' : '#94a3b8'};line-height:1.3;">${item.label}</span>
              </div>`;
            }).join('')}
          </div>`
        : `<div>
            ${sec.items.map(item => {
              const checked = !!checks[`${sec.id}.${item.id}`];
              return `<div style="display:flex;align-items:center;padding:5px 8px;border-bottom:1px solid #f1f5f9;gap:7px;">
                <div style="flex-shrink:0;width:16px;height:16px;border-radius:4px;
                  background:${checked ? accentColor : '#e2e8f0'};
                  border:1.5px solid ${checked ? accentColor : '#cbd5e1'};
                  display:flex;align-items:center;justify-content:center;">
                  ${checked ? `<svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>` : ''}
                </div>
                <span style="font-size:11px;color:${checked ? '#0f172a' : '#94a3b8'};line-height:1.3;">${item.label}</span>
              </div>`;
            }).join('')}
          </div>`;

      seccionesHtml += `
        <div style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin-bottom:${isLast ? '0' : '12px'};">
          <!-- Cabecera sección -->
          <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px;
            background:linear-gradient(90deg,${accentColor}18,${accentColor}06);
            border-bottom:1px solid ${accentColor}25;">
            <div style="display:flex;align-items:center;gap:7px;">
              <span style="font-size:15px;line-height:1;">${sec.emoji}</span>
              <span style="font-size:12px;font-weight:700;color:#1e293b;">${sec.titulo}</span>
            </div>
            <div style="display:flex;align-items:center;gap:8px;">
              <div style="height:5px;width:80px;background:#e2e8f0;border-radius:10px;overflow:hidden;">
                <div style="height:100%;width:${pctSec}%;background:${accentColor};border-radius:10px;transition:width .3s;"></div>
              </div>
              <span style="font-size:10px;font-weight:700;color:${accentColor};min-width:28px;text-align:right;">${pctSec}%</span>
            </div>
          </div>
          <!-- Ítems -->
          ${cols}
          ${sectionNote ? `
          <div style="padding:7px 12px;background:#fffbeb;border-top:1px solid #fcd34d;display:flex;align-items:flex-start;gap:6px;">
            <span style="font-size:12px;flex-shrink:0;">📝</span>
            <span style="font-size:11px;color:#78350f;line-height:1.5;"><strong>Notas:</strong> ${sectionNote}</span>
          </div>` : ''}
        </div>`;
    });

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Ficha RH ${folio} — ${candidato?.nombre_completo || 'Candidato'}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{
      font-family:'Inter',Arial,sans-serif;
      background:#eef2f7;
      color:#1e293b;
      font-size:12px;
      padding:24px 16px 40px;
      min-height:100vh;
    }
    .page{
      max-width:820px;
      margin:0 auto;
      background:#fff;
      border-radius:16px;
      overflow:hidden;
      box-shadow:0 8px 48px rgba(0,0,0,.14),0 2px 12px rgba(0,0,0,.08);
    }
    /* ── Barra flotante ── */
    .toolbar{
      position:fixed;top:16px;right:16px;
      display:flex;gap:8px;z-index:9999;
    }
    .btn-print{
      display:flex;align-items:center;gap:7px;
      padding:10px 22px;
      background:linear-gradient(135deg,${accentColor},${accentColor}cc);
      color:#fff;border:none;border-radius:10px;
      font-family:'Inter',Arial,sans-serif;
      font-size:13px;font-weight:700;
      cursor:pointer;
      box-shadow:0 4px 16px ${accentColor}55;
      transition:transform .15s,box-shadow .15s;
    }
    .btn-print:hover{transform:translateY(-2px);box-shadow:0 6px 24px ${accentColor}77}
    .btn-close{
      display:flex;align-items:center;gap:5px;
      padding:10px 16px;
      background:#334155;color:#fff;
      border:none;border-radius:10px;
      font-family:'Inter',Arial,sans-serif;
      font-size:13px;font-weight:600;
      cursor:pointer;
      box-shadow:0 2px 8px rgba(0,0,0,.2);
    }
    /* ── Header ── */
    .doc-header{
      background:linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#0f172a 100%);
      padding:28px 32px 24px;
      color:#fff;
      position:relative;
      overflow:hidden;
    }
    .doc-header::before{
      content:'';position:absolute;
      right:-30px;top:-30px;
      width:180px;height:180px;
      border-radius:50%;
      background:${accentColor}18;
    }
    .doc-header::after{
      content:'';position:absolute;
      right:60px;bottom:-40px;
      width:120px;height:120px;
      border-radius:50%;
      background:${accentColor}0e;
    }
    .header-accent-bar{
      position:absolute;left:0;top:0;bottom:0;width:5px;
      background:linear-gradient(180deg,${accentColor},${accentColor}88);
    }
    /* ── Folio badge ── */
    .folio-badge{
      display:inline-flex;align-items:center;gap:6px;
      background:${accentColor};
      color:#fff;
      font-size:13px;font-weight:800;
      letter-spacing:1.5px;
      padding:5px 14px;
      border-radius:20px;
      box-shadow:0 2px 12px ${accentColor}66;
      margin-bottom:12px;
    }
    /* ── Candidate section ── */
    .candidate-header{
      padding:20px 32px;
      background:linear-gradient(180deg,#f8fafc,#fff);
      border-bottom:1px solid #e2e8f0;
      display:flex;align-items:center;gap:18px;
    }
    .avatar{
      width:56px;height:56px;border-radius:14px;
      background:linear-gradient(135deg,${accentColor},${accentColor}bb);
      display:flex;align-items:center;justify-content:center;
      font-size:20px;font-weight:800;color:#fff;
      flex-shrink:0;
      box-shadow:0 4px 14px ${accentColor}44;
    }
    .info-grid{
      display:grid;grid-template-columns:repeat(3,1fr);gap:10px 16px;
      padding:16px 32px 20px;
      background:#f8fafc;
      border-bottom:1px solid #e2e8f0;
    }
    .info-cell .label{
      font-size:9px;font-weight:700;letter-spacing:.8px;
      color:#94a3b8;text-transform:uppercase;margin-bottom:2px;
    }
    .info-cell .value{font-size:12px;font-weight:600;color:#1e293b;}
    /* ── Progress bar ── */
    .progress-section{
      padding:14px 32px;
      background:#fff;
      border-bottom:2px solid #e2e8f0;
      display:flex;align-items:center;gap:16px;
    }
    /* ── Content ── */
    .content{padding:24px 32px;}
    .section-title{
      font-size:13px;font-weight:800;
      color:#0f172a;
      margin-bottom:14px;
      display:flex;align-items:center;gap:8px;
      text-transform:uppercase;letter-spacing:.5px;
    }
    .section-title::before{
      content:'';display:inline-block;
      width:4px;height:16px;
      background:${accentColor};
      border-radius:2px;
    }
    /* ── Evaluación ── */
    .eval-box{
      border:2px solid ${nivelData ? nivelData.color : '#e2e8f0'};
      border-radius:12px;
      overflow:hidden;
      margin-bottom:20px;
    }
    .eval-header{
      padding:10px 16px;
      background:${nivelData ? nivelData.color + '14' : '#f8fafc'};
      border-bottom:1px solid ${nivelData ? nivelData.color + '30' : '#e2e8f0'};
      font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;
      color:${nivelData ? nivelData.color : '#64748b'};
    }
    .eval-result{
      padding:14px 16px;
      display:flex;align-items:center;gap:12px;
    }
    .eval-emoji{font-size:28px;line-height:1;}
    .eval-label{font-size:16px;font-weight:800;color:${nivelData ? nivelData.color : '#94a3b8'};}
    .eval-desc{font-size:11px;color:#64748b;margin-top:2px;}
    /* ── Observaciones ── */
    .obs-box{
      background:#f8fafc;
      border:1px solid #e2e8f0;
      border-left:4px solid ${accentColor};
      border-radius:8px;
      padding:12px 16px;
      margin-bottom:20px;
    }
    .obs-label{font-size:9px;font-weight:700;letter-spacing:.8px;color:#94a3b8;text-transform:uppercase;margin-bottom:6px;}
    .obs-text{font-size:12px;line-height:1.8;color:#1e293b;white-space:pre-wrap;}
    /* ── Firmas ── */
    .signatures{
      display:grid;grid-template-columns:1fr 1fr;gap:40px;
      padding:0 32px 24px;
    }
    .sign-block{text-align:center;}
    .sign-line{height:1px;background:#94a3b8;margin-bottom:6px;margin-top:48px;}
    .sign-name{font-size:11px;font-weight:600;color:#475569;}
    .sign-title{font-size:10px;color:#94a3b8;margin-top:2px;}
    /* ── Footer ── */
    .doc-footer{
      background:#0f172a;
      padding:12px 32px;
      display:flex;justify-content:space-between;align-items:center;
      color:rgba(255,255,255,.4);font-size:10px;
    }
    .footer-folio{
      font-size:10px;font-weight:700;letter-spacing:1px;
      color:${accentColor};
    }
    /* ── Print ── */
    @media print{
      body{background:#fff;padding:0;}
      .page{border-radius:0;box-shadow:none;max-width:100%;}
      .toolbar{display:none!important;}
      @page{margin:0;size:A4;}
    }
  </style>
</head>
<body>

  <!-- Barra flotante (solo pantalla) -->
  <div class="toolbar no-print">
    <button class="btn-print" onclick="window.print()">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
      Imprimir / Guardar PDF
    </button>
    <button class="btn-close" onclick="window.close()">✕ Cerrar</button>
  </div>

  <div class="page">

    <!-- ══ HEADER ══ -->
    <div class="doc-header">
      <div class="header-accent-bar"></div>
      <div style="position:relative;z-index:1;">
        <div class="folio-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
          FOLIO: ${folio}
        </div>
        <div style="display:flex;justify-content:space-between;align-items:flex-end;">
          <div>
            <div style="font-size:24px;font-weight:900;letter-spacing:2px;color:#fff;line-height:1;">
              A LA VUELTA <span style="color:${accentColor};">360°</span>
            </div>
            <div style="font-size:10.5px;color:rgba(255,255,255,.45);margin-top:4px;letter-spacing:.5px;">
              DEPARTAMENTO DE RECURSOS HUMANOS &nbsp;·&nbsp; FICHA DE ENTREVISTA &nbsp;·&nbsp; CONFIDENCIAL
            </div>
          </div>
          <div style="text-align:right;font-size:10.5px;color:rgba(255,255,255,.45);line-height:1.8;">
            <div style="color:rgba(255,255,255,.7);font-weight:600;">Fecha de postulación</div>
            <div>${fechaPost}</div>
            <div style="margin-top:4px;color:rgba(255,255,255,.7);font-weight:600;">Generado</div>
            <div>${fechaGen} · ${horaGen}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ CANDIDATO ══ -->
    <div class="candidate-header">
      <div class="avatar">${iniciales}</div>
      <div style="flex:1;min-width:0;">
        <div style="font-size:18px;font-weight:800;color:#0f172a;line-height:1.2;">
          ${candidato?.nombre_completo || '—'}
        </div>
        <div style="font-size:12px;color:#64748b;margin-top:3px;">
          ${template.icon} ${candidato?.puesto_interes || '—'}
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:9px;font-weight:700;letter-spacing:.8px;color:#94a3b8;text-transform:uppercase;margin-bottom:4px;">Estado</div>
        <div style="display:inline-block;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700;
          background:${accentColor}18;color:${accentColor};border:1.5px solid ${accentColor}40;">
          ${(candidato?.estado || '').toUpperCase().replace(/_/g,' ') || 'EN PROCESO'}
        </div>
      </div>
    </div>

    <!-- ══ INFO GRID ══ -->
    <div class="info-grid">
      ${[
        ['Email',    candidato?.email    || '—'],
        ['Teléfono', candidato?.telefono  || '—'],
        ['Ciudad',   candidato?.ciudad    || '—'],
      ].map(([k,v]) => `
        <div class="info-cell">
          <div class="label">${k}</div>
          <div class="value">${v}</div>
        </div>`).join('')}
    </div>

    <!-- ══ BARRA DE PROGRESO ══ -->
    <div class="progress-section">
      <div style="flex:1;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
          <span style="font-size:11px;font-weight:700;color:#475569;">Avance de la evaluación</span>
          <span style="font-size:13px;font-weight:800;color:${accentColor};">${progress}%</span>
        </div>
        <div style="height:8px;background:#e2e8f0;border-radius:10px;overflow:hidden;">
          <div style="height:100%;width:${progress}%;background:linear-gradient(90deg,${accentColor},${accentColor}bb);border-radius:10px;"></div>
        </div>
        <div style="margin-top:5px;font-size:10px;color:#94a3b8;">${checkedCount} de ${totalItems} criterios evaluados</div>
      </div>
      <div style="text-align:center;padding:8px 18px;background:${accentColor}10;
        border:1.5px solid ${accentColor}30;border-radius:10px;min-width:80px;">
        <div style="font-size:22px;font-weight:900;color:${accentColor};line-height:1;">${progress}%</div>
        <div style="font-size:9px;font-weight:600;color:#64748b;margin-top:2px;text-transform:uppercase;letter-spacing:.5px;">Completado</div>
      </div>
    </div>

    <!-- ══ SECCIONES DEL CHECKLIST ══ -->
    <div class="content">
      <div class="section-title">Criterios de Evaluación</div>
      ${seccionesHtml}
    </div>

    <!-- ══ EVALUACIÓN FINAL ══ -->
    <div style="padding:0 32px 20px;">
      <div class="section-title">Evaluación del Entrevistador</div>
      <div class="eval-box">
        <div class="eval-header">⭐ Resultado de la entrevista</div>
        <div class="eval-result">
          ${nivelData
            ? `<div class="eval-emoji">${nivelData.emoji}</div>
               <div>
                 <div class="eval-label">${nivelData.label}</div>
                 <div class="eval-desc">${nivelData.desc}</div>
               </div>`
            : `<div style="color:#94a3b8;font-style:italic;padding:4px 0;">Sin evaluación registrada</div>`}
        </div>
      </div>
      ${obsGen
        ? `<div class="obs-box">
             <div class="obs-label">Observaciones Generales del Entrevistador</div>
             <div class="obs-text">${obsGen}</div>
           </div>`
        : ''}
    </div>

    <!-- ══ FIRMAS ══ -->
    <div class="signatures">
      <div class="sign-block">
        <div class="sign-line"></div>
        <div class="sign-name">${entrevistador}</div>
        <div class="sign-title">Entrevistador / Recursos Humanos · A La Vuelta 360°</div>
      </div>
      <div class="sign-block">
        <div class="sign-line"></div>
        <div class="sign-name">${candidato?.nombre_completo || 'Candidato'}</div>
        <div class="sign-title">Candidato</div>
      </div>
    </div>

    <!-- ══ FOOTER ══ -->
    <div class="doc-footer">
      <div>
        <span>A La Vuelta 360° &nbsp;·&nbsp; Recursos Humanos &nbsp;·&nbsp; Documento Confidencial</span>
      </div>
      <div style="display:flex;align-items:center;gap:12px;">
        <span>Generado el ${fechaGen}</span>
        <span class="footer-folio">${folio}</span>
      </div>
    </div>

  </div><!-- /page -->
</body></html>`;

    win.document.write(html);
    win.document.close();
    win.focus();
  };

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8, pb: 2 }}>

      {/* ── PROGRESS HEADER ── */}
      <Box sx={{
        p: 2, borderRadius: "16px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        {/* Circular progress */}
        <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
          <CircularProgress
            variant="determinate" value={100} size={68} thickness={4.5}
            sx={{ color: "rgba(255,255,255,0.06)", position: "absolute" }}
          />
          <CircularProgress
            variant="determinate" value={progress} size={68} thickness={4.5}
            sx={{
              color: progressColor,
              "& .MuiCircularProgress-circle": { strokeLinecap: "round", transition: "stroke-dashoffset 0.45s ease" },
            }}
          />
          <Box sx={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center", flexDirection: "column",
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: progressColor, lineHeight: 1 }}>
              {progress}%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", display: "flex", alignItems: "center", gap: 0.5 }}>
            {template.icon} {template.titulo}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {checkedCount} de {totalItems} ítems evaluados
          </Typography>
          <Box sx={{ mt: 0.8, height: 3.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
            <Box sx={{
              height: "100%", borderRadius: 2, transition: "width 0.4s ease",
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${progressColor}, ${progressColor}90)`,
            }} />
          </Box>
        </Box>

        {/* Estado badge */}
        <Chip
          size="small"
          label={
            progress === 100 ? "✓ Completo" :
            dirty            ? "Sin guardar" : "Guardado"
          }
          sx={{
            borderRadius: "8px", fontSize: "0.67rem", fontWeight: 700, flexShrink: 0,
            ...(progress === 100
              ? { bgcolor: "rgba(34,197,94,0.1)",   color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)" }
              : dirty
              ? { bgcolor: "rgba(245,158,11,0.1)",  color: "#f59e0b", border: "1px solid rgba(245,158,11,0.25)" }
              : { bgcolor: "rgba(255,255,255,0.05)", color: "text.disabled", border: "1px solid rgba(255,255,255,0.08)" }),
          }}
        />
      </Box>

      {/* ── SECCIONES ── */}
      {template.secciones.map(seccion => (
        <SeccionChecklist
          key={seccion.id}
          seccion={seccion}
          checks={checks}
          onToggle={toggleCheck}
          notes={notes[seccion.id]}
          onNoteChange={handleNoteChange}
          templateColor={template.color}
        />
      ))}

      {/* ── EVALUACIÓN FINAL ── */}
      <Box sx={{ p: 2, border: "1px solid rgba(255,255,255,0.08)", borderRadius: "14px" }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}>
          ⭐ Evaluación del entrevistador
        </Typography>

        {/* Niveles de recomendación */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
          {NIVELES_EVALUACION.map((n) => {
            const sel = nivel === n.id;
            return (
              <Tooltip key={n.id} title={n.desc} placement="top" arrow>
                <Box
                  onClick={() => { setNivel(sel ? null : n.id); setDirty(true); }}
                  sx={{
                    p: "10px 12px", borderRadius: "12px", cursor: "pointer",
                    border: `1px solid ${sel ? n.color + "55" : "rgba(255,255,255,0.07)"}`,
                    bgcolor: sel ? `${n.color}14` : "rgba(255,255,255,0.02)",
                    transition: "all 0.18s",
                    "&:hover": { bgcolor: `${n.color}10`, borderColor: `${n.color}40` },
                    userSelect: "none",
                  }}
                >
                  <Typography sx={{ fontSize: "1.1rem", mb: 0.3, lineHeight: 1 }}>{n.emoji}</Typography>
                  <Typography variant="body2" sx={{
                    fontWeight: sel ? 700 : 500, fontSize: "0.78rem", lineHeight: 1.3,
                    color: sel ? n.color : "text.primary",
                  }}>
                    {n.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem" }}>
                    {n.desc}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Observaciones generales */}
        <TextField
          multiline rows={3} fullWidth size="small"
          label="Observaciones generales del entrevistador"
          placeholder="Impresión general, puntos fuertes, áreas de oportunidad, recomendación final…"
          value={obsGen}
          onChange={e => { setObsGen(e.target.value); setDirty(true); }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px", fontSize: "0.82rem",
              "& fieldset": {
                borderColor: dirty && !hasObservaciones
                  ? "rgba(239,68,68,0.6)"
                  : "rgba(255,255,255,0.1)",
              },
              "&.Mui-focused fieldset": { borderColor: `${template.color}70` },
            },
          }}
        />
        {dirty && !hasObservaciones && (
          <Typography variant="caption" sx={{ color: "#f87171", mt: 0.5, display: "block", fontSize: "0.72rem" }}>
            ⚠ Escribe tus observaciones para poder guardar
          </Typography>
        )}
      </Box>

      {/* ── HINT DE VALIDACIÓN ── */}
      {dirty && (!allSectionsHaveCheck || !hasObservaciones) && (
        <Box sx={{
          p: 1.5, borderRadius: "10px",
          background: "rgba(245,158,11,0.08)",
          border: "1px solid rgba(245,158,11,0.25)",
          display: "flex", flexDirection: "column", gap: 0.5,
        }}>
          <Typography variant="caption" sx={{ color: "#fbbf24", fontWeight: 700, fontSize: "0.75rem" }}>
            Para guardar completa lo siguiente:
          </Typography>
          {sectionsWithoutCheck.map(sec => (
            <Typography key={sec.id} variant="caption" sx={{ color: "rgba(251,191,36,0.75)", fontSize: "0.72rem", pl: 1 }}>
              • {sec.emoji} {sec.titulo} — falta al menos un ✓
            </Typography>
          ))}
          {!hasObservaciones && (
            <Typography variant="caption" sx={{ color: "rgba(251,191,36,0.75)", fontSize: "0.72rem", pl: 1 }}>
              • Observaciones generales — campo obligatorio
            </Typography>
          )}
        </Box>
      )}

      {/* ── BOTÓN GUARDAR ── */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={saving || !canSave}
        startIcon={
          saving
            ? <CircularProgress size={16} color="inherit" />
            : canSave ? <Save /> : <CloudDone />
        }
        sx={{
          borderRadius: "12px", textTransform: "none", fontWeight: 700, fontSize: "0.9rem",
          background: canSave
            ? `linear-gradient(90deg, ${template.color}, ${template.color}cc)`
            : "rgba(255,255,255,0.06)",
          color: canSave ? "#fff" : "rgba(255,255,255,0.25)",
          "&:disabled": { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.2)" },
          "&:hover:not(:disabled)": { transform: "translateY(-1px)", boxShadow: `0 6px 20px ${template.color}40` },
          transition: "all 0.2s",
        }}
      >
        {saving ? "Guardando…" : canSave ? "Guardar progreso" : "Sin cambios pendientes"}
      </Button>

      {/* ── BOTÓN GENERAR PDF ── */}
      {!dirty && initialData && (
        <Button
          variant="outlined"
          fullWidth
          onClick={handleGeneratePDF}
          startIcon={<PictureAsPdf />}
          sx={{
            borderRadius: "12px", textTransform: "none", fontWeight: 700, fontSize: "0.9rem",
            borderColor: "rgba(239,68,68,0.5)", color: "#f87171",
            "&:hover": {
              background: "rgba(239,68,68,0.08)",
              borderColor: "#ef4444",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
            },
            transition: "all 0.2s",
          }}
        >
          Generar PDF / Imprimir ficha RH
        </Button>
      )}

    </Box>
  );
};

export default InterviewChecklist;
